/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    BusinessPartnerUser,
    InvoicePaidStatus,
    OrderInvoice,
    SalesInvoice,
    SalesInvoiceLine,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { IPayableInvoice } from '@msdyn365-commerce-modules/invoice';
import { IInvoiceDetailsResources } from '../invoice-details.props.autogenerated';

export interface IInvoicePaymentDetails {
    totalDiscount?: number;
    subtotal?: number;
    totalCharges?: number;
    totalTax?: number;
    totalAmount?: number;
    currencyCode?: string;
}

export interface IInvoiceProductLine extends SalesInvoiceLine {
    primaryImageUrl?: string;
}

/**
 * Represents invoice details data model.
 */
export class InvoiceDetailsModel implements IPayableInvoice, IInvoicePaymentDetails {
    public id: string;

    public invoiceDate?: Date;

    public customerAccountNumber?: string;

    public customerName?: string;

    public dueDate?: Date;

    public amountDue?: number;

    public status?: InvoicePaidStatus;

    public lines: IInvoiceProductLine[];

    public totalDiscount?: number;

    public subtotal?: number;

    public totalCharges?: number;

    public totalTax?: number;

    public totalAmount?: number;

    public currencyCode?: string;

    public orderFor?: string;

    public constructor(
        invoice: OrderInvoice,
        invoiceDetails: SalesInvoice,
        products: SimpleProduct[],
        currentBusinessPartnerUser?: BusinessPartnerUser,
        isContactModelInvoice?: boolean,
        resources?: IInvoiceDetailsResources,
        currentStaffId?: string
    ) {
        this.id = invoice.Id || '';
        this.invoiceDate = invoice.InvoiceDate ? new Date(invoice.InvoiceDate) : undefined;
        this.customerName = invoice.CustomerName;
        this.dueDate = invoice.InvoiceDueDate ? new Date(invoice.InvoiceDueDate) : undefined;
        this.amountDue = invoice.AmountBalance;
        this.status = invoice.InvoicePaidStatusValue;
        this.lines = (invoiceDetails.SalesInvoiceLine || []).map(line => {
            const productLine: IInvoiceProductLine = {
                ...line,
                primaryImageUrl: products.find(product => product.RecordId === line.ProductId)?.PrimaryImageUrl
            };
            return productLine;
        });

        this.totalDiscount = invoiceDetails.TotalDiscount;
        this.subtotal = invoiceDetails.InvoiceSubtotal;
        this.totalCharges = invoiceDetails.TotalCharges;
        this.totalTax = invoiceDetails.TotalTaxAmount;
        this.totalAmount = invoice.TotalAmount;
        this.currencyCode = invoice.CurrencyCode;

        this._setRetailB2BCustomerName(invoice, resources, currentBusinessPartnerUser, isContactModelInvoice, currentStaffId);
    }

    public get canBePaid(): boolean {
        return this.status !== InvoicePaidStatus.Paid;
    }

    private readonly _setRetailB2BCustomerName = (
        invoice: OrderInvoice,
        resources?: IInvoiceDetailsResources,
        currentBusinessPartnerUser?: BusinessPartnerUser,
        isContactModelInvoice?: boolean,
        currentStaffId?: string
    ) => {
        if (!isContactModelInvoice) {
            return;
        }

        const { CustomerName, StaffId, StaffName, ContactPersonId, ContactPersonName } = invoice;
        const { invoicePlacedByYouLabel = '', invoiceOnBehalfOfLabel = '' } = resources ?? {};

        this.orderFor = CustomerName;
        const staffPlacedOrder = !!StaffId && !!StaffName;

        if (staffPlacedOrder) {
            // OBO: With staffId, see if the staff is current staff
            const you = currentStaffId === StaffId ? invoicePlacedByYouLabel : '';
            const onBehalfOf = invoiceOnBehalfOfLabel
                ? `${invoiceOnBehalfOfLabel} ${
                      ContactPersonId && ContactPersonName ? `${ContactPersonName} (${CustomerName})` : CustomerName
                  }`
                : '';

            this.customerName = [StaffName, you, onBehalfOf].filter(Boolean).join(' ');
        } else {
            // Non-OBO: no StaffId, see if contact person is current user
            const isCurrentContact =
                !!currentBusinessPartnerUser?.ContactPersonId && currentBusinessPartnerUser.ContactPersonId === ContactPersonId;

            if (isCurrentContact) {
                const displayName = ContactPersonName ?? CustomerName ?? '';
                this.customerName = [displayName, invoicePlacedByYouLabel].filter(Boolean).join(' ');
            } else if (ContactPersonId && ContactPersonName) {
                this.customerName = `${ContactPersonName} (${CustomerName})`;
            } else {
                this.customerName = ContactPersonName ?? CustomerName ?? '';
            }
        }
    };
}
